/* ═══════════════════════════════════════════════════════
   COMPANY TYPE PRESETS
   Auto-configuration per industry/company type
   Used by OnboardingPage and AdminCenter
   ═══════════════════════════════════════════════════════ */

const COLORS = {
  slate: "#535C6E", amber: "#FFB547", purple: "#7C5CFC",
  blue: "#FAFAFA", navy: "#4059AD", green: "#00D68F",
  red: "#E53935", orange: "#FF6D00", cyan: "#00B8D4", pink: "#C51162",
};

const COMPANY_TYPES = {
  saas: {
    label: "SaaS / Software",
    icon: "code",
    description: "B2B software, subscriptions, and platform sales",
    pipeline: {
      name: "SaaS Sales Pipeline",
      stages: [
        { name: "Discovery", prob: 10, color: COLORS.slate },
        { name: "Qualification", prob: 25, color: COLORS.amber },
        { name: "Demo", prob: 40, color: COLORS.navy },
        { name: "Proposal", prob: 60, color: COLORS.purple },
        { name: "Negotiation", prob: 80, color: COLORS.blue },
        { name: "Closed Won", prob: 100, color: COLORS.green },
      ],
    },
    kpiTargets: {
      sdr: { dials: 80, emails: 40, meetings: 3 },
      ae: { dials: 30, emails: 20, meetings: 5 },
      manager: { dials: 10, emails: 15, meetings: 8 },
    },
    leadScoring: {
      weights: {
        emailOpened: 5, emailClicked: 10, emailReplied: 20,
        callConnected: 15, callOver5Min: 25,
        meetingBooked: 30, meetingAttended: 40,
        proposalViewed: 35, websiteVisit: 3,
        demoRequested: 50, trialStarted: 60,
      },
      hotThreshold: 75,
      warmThreshold: 40,
    },
    dashboardPresets: {
      sales: { widgets: ["pipeline", "forecast", "activities", "leaderboard", "dealVelocity"] },
      admin: { widgets: ["teamPerformance", "revenue", "churn", "pipeline", "forecast"] },
      marketing: { widgets: ["leadSources", "conversionFunnel", "campaignROI", "webTraffic"] },
    },
    emailTemplates: [
      { name: "Cold Outreach", subject: "Quick question about {{company}}", body: "Hi {{firstName}},\n\nI noticed {{company}} is growing fast in {{industry}}. We help companies like yours {{value_prop}}.\n\nWould you be open to a quick 15-minute call this week?\n\nBest,\n{{senderName}}" },
      { name: "Demo Follow-up", subject: "Great chatting — next steps", body: "Hi {{firstName}},\n\nGreat demo today! As we discussed, here's a quick recap:\n\n- {{key_point_1}}\n- {{key_point_2}}\n\nI'll send over the proposal by {{date}}. Let me know if you have any questions.\n\nBest,\n{{senderName}}" },
      { name: "Trial Check-in", subject: "How's the trial going?", body: "Hi {{firstName}},\n\nJust checking in on your trial. Have you had a chance to explore {{feature}}?\n\nHappy to jump on a call if you need help getting set up.\n\nBest,\n{{senderName}}" },
    ],
    smsTemplates: [
      { name: "Meeting Reminder", body: "Hi {{firstName}}, just a reminder about our call today at {{time}}. Looking forward to it! - {{senderName}}" },
      { name: "Quick Follow-up", body: "Hi {{firstName}}, following up on our conversation. Any questions I can help with? - {{senderName}}" },
    ],
  },

  realestate: {
    label: "Real Estate",
    icon: "home",
    description: "Residential and commercial property sales",
    pipeline: {
      name: "Real Estate Pipeline",
      stages: [
        { name: "New Lead", prob: 10, color: COLORS.slate },
        { name: "Contacted", prob: 20, color: COLORS.amber },
        { name: "Showing Scheduled", prob: 35, color: COLORS.navy },
        { name: "Offer Submitted", prob: 55, color: COLORS.purple },
        { name: "Under Contract", prob: 80, color: COLORS.blue },
        { name: "Closed", prob: 100, color: COLORS.green },
      ],
    },
    kpiTargets: {
      sdr: { dials: 50, emails: 20, meetings: 5 },
      ae: { dials: 30, emails: 15, meetings: 8 },
      manager: { dials: 15, emails: 10, meetings: 10 },
    },
    leadScoring: {
      weights: {
        emailOpened: 3, emailClicked: 8, emailReplied: 15,
        callConnected: 20, callOver5Min: 30,
        meetingBooked: 25, meetingAttended: 35,
        propertyViewed: 20, openHouseAttended: 40,
        preApproved: 50, offerSubmitted: 70,
      },
      hotThreshold: 70,
      warmThreshold: 35,
    },
    dashboardPresets: {
      sales: { widgets: ["pipeline", "showings", "activities", "hotLeads", "closings"] },
      admin: { widgets: ["teamPerformance", "revenue", "listings", "pipeline", "closings"] },
      marketing: { widgets: ["leadSources", "openHouseROI", "webTraffic", "adPerformance"] },
    },
    emailTemplates: [
      { name: "New Listing Alert", subject: "New listing you'll love in {{area}}", body: "Hi {{firstName}},\n\nA property just hit the market that matches what you're looking for:\n\n{{property_details}}\n\nWould you like to schedule a showing?\n\nBest,\n{{senderName}}" },
      { name: "Post-Showing Follow-up", subject: "Thoughts on {{property_address}}?", body: "Hi {{firstName}},\n\nGreat seeing you at the showing today! What did you think of the property?\n\nI have a few similar listings I think you'd love. Want me to send them over?\n\nBest,\n{{senderName}}" },
    ],
    smsTemplates: [
      { name: "Showing Reminder", body: "Hi {{firstName}}, reminder about your showing at {{address}} today at {{time}}. See you there! - {{senderName}}" },
      { name: "New Listing", body: "Hi {{firstName}}, new listing just dropped in {{area}} that matches your criteria. Want details? - {{senderName}}" },
    ],
  },

  insurance: {
    label: "Insurance / Financial Services",
    icon: "shield",
    description: "Insurance policies, financial products, and advisory",
    pipeline: {
      name: "Insurance Sales Pipeline",
      stages: [
        { name: "Lead", prob: 10, color: COLORS.slate },
        { name: "Contacted", prob: 20, color: COLORS.amber },
        { name: "Needs Analysis", prob: 35, color: COLORS.navy },
        { name: "Quote Presented", prob: 55, color: COLORS.purple },
        { name: "Application", prob: 75, color: COLORS.blue },
        { name: "Policy Bound", prob: 100, color: COLORS.green },
      ],
    },
    kpiTargets: {
      sdr: { dials: 100, emails: 30, meetings: 4 },
      ae: { dials: 40, emails: 20, meetings: 6 },
      manager: { dials: 15, emails: 10, meetings: 8 },
    },
    leadScoring: {
      weights: {
        emailOpened: 5, emailClicked: 10, emailReplied: 20,
        callConnected: 20, callOver5Min: 30,
        meetingBooked: 25, meetingAttended: 35,
        quoteRequested: 45, applicationStarted: 60,
        referralReceived: 40, policyRenewal: 50,
      },
      hotThreshold: 70,
      warmThreshold: 35,
    },
    dashboardPresets: {
      sales: { widgets: ["pipeline", "policies", "activities", "renewals", "referrals"] },
      admin: { widgets: ["teamPerformance", "revenue", "retentionRate", "pipeline", "claims"] },
      marketing: { widgets: ["leadSources", "referralNetwork", "campaignROI", "retention"] },
    },
    emailTemplates: [
      { name: "Policy Review", subject: "Time for your annual policy review", body: "Hi {{firstName}},\n\nIt's been a year since we last reviewed your coverage. A lot can change — let's make sure you're still properly protected.\n\nI have a few openings this week. Does {{date}} work?\n\nBest,\n{{senderName}}" },
      { name: "Quote Follow-up", subject: "Your personalized quote is ready", body: "Hi {{firstName}},\n\nI've put together a customized quote based on our conversation. Here are the highlights:\n\n{{quote_summary}}\n\nLet me know if you'd like to go over it together.\n\nBest,\n{{senderName}}" },
    ],
    smsTemplates: [
      { name: "Appointment Reminder", body: "Hi {{firstName}}, reminder about our policy review call tomorrow at {{time}}. - {{senderName}}" },
      { name: "Renewal Notice", body: "Hi {{firstName}}, your {{policy_type}} policy renews next month. Want to review your options? - {{senderName}}" },
    ],
  },

  agency: {
    label: "Agency / Consulting",
    icon: "chart",
    description: "Marketing agencies, consulting firms, and professional services",
    pipeline: {
      name: "Agency Pipeline",
      stages: [
        { name: "Inquiry", prob: 10, color: COLORS.slate },
        { name: "Discovery Call", prob: 25, color: COLORS.amber },
        { name: "Strategy Session", prob: 40, color: COLORS.navy },
        { name: "Proposal Sent", prob: 60, color: COLORS.purple },
        { name: "Negotiation", prob: 80, color: COLORS.blue },
        { name: "Won", prob: 100, color: COLORS.green },
      ],
    },
    kpiTargets: {
      sdr: { dials: 40, emails: 30, meetings: 5 },
      ae: { dials: 20, emails: 25, meetings: 6 },
      manager: { dials: 10, emails: 15, meetings: 8 },
    },
    leadScoring: {
      weights: {
        emailOpened: 5, emailClicked: 12, emailReplied: 25,
        callConnected: 15, callOver5Min: 25,
        meetingBooked: 30, meetingAttended: 40,
        proposalViewed: 35, caseStudyViewed: 20,
        referralReceived: 45, retainerSigned: 80,
      },
      hotThreshold: 75,
      warmThreshold: 40,
    },
    dashboardPresets: {
      sales: { widgets: ["pipeline", "forecast", "activities", "clientHealth", "proposals"] },
      admin: { widgets: ["teamPerformance", "revenue", "utilization", "pipeline", "clientRetention"] },
      marketing: { widgets: ["leadSources", "contentPerformance", "referralNetwork", "brandAwareness"] },
    },
    emailTemplates: [
      { name: "Discovery Request", subject: "Let's explore how we can help {{company}}", body: "Hi {{firstName}},\n\nI took a look at {{company}} and have a few ideas on how we could help with {{pain_point}}.\n\nWould you be open to a 20-minute discovery call?\n\nBest,\n{{senderName}}" },
      { name: "Proposal Follow-up", subject: "Checking in on the proposal", body: "Hi {{firstName}},\n\nJust following up on the proposal I sent over. Happy to walk through any questions or adjust the scope.\n\nWhen works for a quick call?\n\nBest,\n{{senderName}}" },
    ],
    smsTemplates: [
      { name: "Meeting Reminder", body: "Hi {{firstName}}, looking forward to our call today at {{time}}. Talk soon! - {{senderName}}" },
    ],
  },

  ecommerce: {
    label: "E-commerce / Retail",
    icon: "cart",
    description: "Online stores, wholesale, and retail operations",
    pipeline: {
      name: "E-commerce Pipeline",
      stages: [
        { name: "Lead", prob: 10, color: COLORS.slate },
        { name: "Contacted", prob: 20, color: COLORS.amber },
        { name: "Quote Sent", prob: 45, color: COLORS.purple },
        { name: "Negotiation", prob: 70, color: COLORS.blue },
        { name: "Order Placed", prob: 90, color: COLORS.navy },
        { name: "Fulfilled", prob: 100, color: COLORS.green },
      ],
    },
    kpiTargets: {
      sdr: { dials: 60, emails: 50, meetings: 2 },
      ae: { dials: 25, emails: 30, meetings: 4 },
      manager: { dials: 10, emails: 20, meetings: 5 },
    },
    leadScoring: {
      weights: {
        emailOpened: 5, emailClicked: 15, emailReplied: 20,
        callConnected: 10, callOver5Min: 20,
        cartAbandoned: 25, productViewed: 8,
        accountCreated: 15, repeatPurchase: 50,
        bulkInquiry: 40, sampleRequested: 35,
      },
      hotThreshold: 65,
      warmThreshold: 30,
    },
    dashboardPresets: {
      sales: { widgets: ["pipeline", "orders", "activities", "topProducts", "revenue"] },
      admin: { widgets: ["teamPerformance", "revenue", "inventory", "pipeline", "fulfillment"] },
      marketing: { widgets: ["conversionFunnel", "cartAbandonment", "campaignROI", "customerLTV"] },
    },
    emailTemplates: [
      { name: "Wholesale Inquiry", subject: "Wholesale pricing for {{company}}", body: "Hi {{firstName}},\n\nThanks for your interest in our products! Based on your inquiry, I've put together volume pricing:\n\n{{pricing_details}}\n\nWould you like to place a sample order?\n\nBest,\n{{senderName}}" },
      { name: "Order Follow-up", subject: "How's everything with your order?", body: "Hi {{firstName}},\n\nJust checking in on your recent order. Everything arrive okay?\n\nWe'd love to earn your repeat business. Here's a {{discount}}% discount on your next order: {{code}}\n\nBest,\n{{senderName}}" },
    ],
    smsTemplates: [
      { name: "Order Shipped", body: "Hi {{firstName}}, your order just shipped! Tracking: {{tracking}}. Thanks for your business! - {{senderName}}" },
    ],
  },

  coaching: {
    label: "Coaching / Online Courses",
    icon: "cap",
    description: "Life coaching, business coaching, courses, and training",
    pipeline: {
      name: "Coaching Pipeline",
      stages: [
        { name: "Interested", prob: 10, color: COLORS.slate },
        { name: "Discovery Call", prob: 25, color: COLORS.amber },
        { name: "Strategy Session", prob: 45, color: COLORS.navy },
        { name: "Proposal", prob: 65, color: COLORS.purple },
        { name: "Enrolled", prob: 90, color: COLORS.blue },
        { name: "Active Client", prob: 100, color: COLORS.green },
      ],
    },
    kpiTargets: {
      sdr: { dials: 40, emails: 25, meetings: 6 },
      ae: { dials: 20, emails: 15, meetings: 8 },
      manager: { dials: 10, emails: 10, meetings: 10 },
    },
    leadScoring: {
      weights: {
        emailOpened: 5, emailClicked: 12, emailReplied: 25,
        callConnected: 20, callOver5Min: 30,
        meetingBooked: 30, meetingAttended: 45,
        webinarAttended: 35, freeResourceDownloaded: 15,
        applicationSubmitted: 55, testimonialGiven: 40,
      },
      hotThreshold: 70,
      warmThreshold: 35,
    },
    dashboardPresets: {
      sales: { widgets: ["pipeline", "enrollments", "activities", "upcomingSessions", "revenue"] },
      admin: { widgets: ["teamPerformance", "revenue", "clientRetention", "pipeline", "courseCompletion"] },
      marketing: { widgets: ["leadSources", "webinarConversion", "contentPerformance", "socialMedia"] },
    },
    emailTemplates: [
      { name: "Free Resource Follow-up", subject: "Did you get a chance to check it out?", body: "Hi {{firstName}},\n\nI noticed you downloaded our {{resource_name}}. What did you think?\n\nIf you're serious about {{goal}}, I'd love to hop on a quick call to see if our program is a fit.\n\nBest,\n{{senderName}}" },
      { name: "Discovery Call Invite", subject: "Let's talk about your goals", body: "Hi {{firstName}},\n\nI help {{audience}} achieve {{outcome}}. Based on what I know about your situation, I think we could make some real progress together.\n\nWant to hop on a free 30-minute strategy call?\n\nBest,\n{{senderName}}" },
    ],
    smsTemplates: [
      { name: "Session Reminder", body: "Hi {{firstName}}, reminder about our coaching session tomorrow at {{time}}. Come prepared with your wins from this week! - {{senderName}}" },
      { name: "Course Launch", body: "Hi {{firstName}}, our new {{course_name}} just launched! Early bird pricing ends {{date}}. Want the link? - {{senderName}}" },
    ],
  },
};

// ── Onboarding Checklist Items ──
// These appear after the wizard as persistent tasks to complete
const ONBOARDING_CHECKLIST = [
  {
    id: "company_type",
    label: "Select your company type",
    description: "Choose your industry so we can configure your CRM automatically",
    category: "setup",
    required: true,
    autoComplete: true, // Completed during wizard
  },
  {
    id: "pipeline",
    label: "Configure your sales pipeline",
    description: "Set up deal stages that match your sales process",
    category: "setup",
    required: true,
    autoComplete: true,
  },
  {
    id: "invite_team",
    label: "Invite your team members",
    description: "Add your sales reps, managers, and admins",
    category: "team",
    required: false,
    route: "admin/users",
  },
  {
    id: "assign_roles",
    label: "Assign team roles & KPI targets",
    description: "Set SDR, AE, or Manager roles with daily activity goals",
    category: "team",
    required: false,
    route: "admin/team",
  },
  {
    id: "connect_email",
    label: "Connect your email account",
    description: "Link Gmail or Outlook to send and track emails from Coreflow",
    category: "integrations",
    required: false,
    route: "admin/integrations",
  },
  {
    id: "setup_dialer",
    label: "Set up your phone system",
    description: "Get a phone number and start making calls from the browser",
    category: "integrations",
    required: false,
    route: "admin/phone-numbers",
  },
  {
    id: "caller_id",
    label: "Configure caller ID",
    description: "Choose which number shows when you make outbound calls",
    category: "integrations",
    required: false,
    route: "admin/phone-numbers",
  },
  {
    id: "import_contacts",
    label: "Import your contacts",
    description: "Upload a CSV or connect your existing CRM — or let us do it for you during onboarding",
    category: "data",
    required: false,
    note: "Our team can handle this for you during onboarding",
  },
  {
    id: "email_signature",
    label: "Set up your email signature",
    description: "Add your professional signature for outbound emails",
    category: "personalization",
    required: false,
    route: "admin/signatures",
  },
  {
    id: "branding",
    label: "Add your company branding",
    description: "Upload your logo and set brand colors",
    category: "personalization",
    required: false,
    route: "admin/general",
  },
  {
    id: "mission_control",
    label: "Try Mission Control",
    description: "Hold Space and say 'show my pipeline' to see AI voice commands in action",
    category: "explore",
    required: false,
    route: "dashboard",
  },
  {
    id: "first_call",
    label: "Make your first call",
    description: "Open the dialer and call a contact directly from Coreflow",
    category: "explore",
    required: false,
    route: "dialer",
  },
  {
    id: "first_email",
    label: "Send your first email",
    description: "Draft and send an email to a contact using Coreflow's email tools",
    category: "explore",
    required: false,
    route: "email",
  },
];

const CHECKLIST_CATEGORIES = {
  setup: { label: "Core Setup", icon: "gear" },
  team: { label: "Team", icon: "users" },
  integrations: { label: "Integrations", icon: "link" },
  data: { label: "Data", icon: "folder" },
  personalization: { label: "Personalization", icon: "palette" },
  explore: { label: "Explore Features", icon: "rocket" },
};

export { COMPANY_TYPES, ONBOARDING_CHECKLIST, CHECKLIST_CATEGORIES, PresetIcon };
export default COMPANY_TYPES;

// Inline SVG icons for company types and categories (no emojis)
function PresetIcon({ name, size = 18, color = "#FAFAFA" }) {
  const s = { width: size, height: size, flexShrink: 0 };
  const paths = {
    code: <><rect x="2" y="3" width="20" height="18" rx="2" stroke={color} strokeWidth="1.5" fill="none"/><polyline points="8,8 5,12 8,16" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/><polyline points="16,8 19,12 16,16" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/><line x1="13" y1="7" x2="11" y2="17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    home: <><path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V10.5z" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round"/><rect x="9" y="14" width="6" height="7" stroke={color} strokeWidth="1.5" fill="none"/></>,
    shield: <><path d="M12 2l8 4v5c0 5.25-3.5 9.74-8 11-4.5-1.26-8-5.75-8-11V6l8-4z" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round"/><polyline points="9,12 11,14 15,10" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    chart: <><rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="1.5" fill="none"/><rect x="7" y="11" width="2" height="6" rx="0.5" fill={color}/><rect x="11" y="8" width="2" height="9" rx="0.5" fill={color}/><rect x="15" y="5" width="2" height="12" rx="0.5" fill={color}/></>,
    cart: <><circle cx="9" cy="20" r="1.5" stroke={color} strokeWidth="1.5" fill="none"/><circle cx="18" cy="20" r="1.5" stroke={color} strokeWidth="1.5" fill="none"/><path d="M1 1h3l2.5 13h12L22 5H6" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    cap: <><path d="M12 3L1 9l11 6 11-6-11-6z" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round"/><path d="M5 12v5c0 2 3.5 4 7 4s7-2 7-4v-5" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/><line x1="22" y1="9" x2="22" y2="17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    gear: <><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5" fill="none"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke={color} strokeWidth="1.5" fill="none"/></>,
    users: <><circle cx="9" cy="7" r="3.5" stroke={color} strokeWidth="1.5" fill="none"/><path d="M2 21v-2a5 5 0 0110 0v2" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/><circle cx="17" cy="8" r="2.5" stroke={color} strokeWidth="1.5" fill="none"/><path d="M16 21v-1.5a4 4 0 014-4" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/></>,
    link: <><path d="M10 14a3.5 3.5 0 005 0l3-3a3.5 3.5 0 00-5-5l-.5.5" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/><path d="M14 10a3.5 3.5 0 00-5 0l-3 3a3.5 3.5 0 005 5l.5-.5" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/></>,
    folder: <><path d="M3 6v12a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-7l-2-3H5a2 2 0 00-2 2z" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round"/></>,
    palette: <><circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" fill="none"/><circle cx="8" cy="10" r="1.2" fill={color}/><circle cx="12" cy="7.5" r="1.2" fill={color}/><circle cx="16" cy="10" r="1.2" fill={color}/><circle cx="8.5" cy="14.5" r="1.2" fill={color}/></>,
    rocket: <><path d="M12 2C8 6 6 10 6 14l6 6c4 0 8-2 12-6-4-4-8-6-12-12z" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round"/><circle cx="12" cy="12" r="2" stroke={color} strokeWidth="1.5" fill="none"/><path d="M6 14l-3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><path d="M18 14l3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
  };
  return <svg viewBox="0 0 24 24" style={s}>{paths[name] || paths.gear}</svg>;
}
